import { model as flags } from '!/flags'
import { model as launch } from '!/launch'
import { firebaseSharedModel as firebase } from '@/firebase'
import { createEvent, sample, type EventCallable } from 'effector'
import { not } from 'patronum'
import { model as session } from '~/entities/session'
import { appsFlyerFeatureModel } from '~/features/appsFlyerTracking'
import { channelsFeature } from '~/features/epg/channels'
import { googlePalFeatureModel } from '~/features/google-pal'
import { preferencesFeatureModel } from '~/features/preferences'
import { profile } from '~/features/profile'
import { model as clock } from '~/shared/clock'
import { PATH } from '~/shared/constants/path'
import {
  extractWrappedId,
  getQueryParamsFromUrl,
  getRegionFromUrl,
} from '~/shared/helpers'

export const init: EventCallable<void> = createEvent()
export const appCanBeLaunched = createEvent()

//
// application launch
//

sample({
  clock: init,
  target: launch.init,
})

sample({
  clock: launch.appCanBeLaunched,
  target: appCanBeLaunched,
})

// setup application clock
sample({
  clock: launch.appCanBeLaunched,
  target: clock.setup,
})

// init flags and features
sample({
  clock: launch.appCanBeLaunched,
  filter: not(launch.$isGeoblock),
  target: flags.init,
})

sample({
  clock: launch.appCanBeLaunched,
  target: firebase.init,
})

// init session (get from BE)
sample({
  clock: launch.appCanBeLaunched,
  target: session.init,
})

// init features
sample({
  clock: launch.appCanBeLaunched,
  target: [
    googlePalFeatureModel.init,
    preferencesFeatureModel.init,
    profile.init,
    appsFlyerFeatureModel.init,
  ],
})

// init live
sample({
  clock: launch.appCanBeLaunched,
  target: channelsFeature.init,
})

// Set primary region from utm_campaign as early as possible
sample({
  clock: launch.appCanBeLaunched,
  filter: () => {
    const { utm_custom_param } = getQueryParamsFromUrl()

    return (
      Boolean(extractWrappedId(utm_custom_param ?? '')) &&
      !window.location.pathname.startsWith(PATH.SETTINGS)
    )
  },
  fn: () => {
    const { utm_custom_param } = getQueryParamsFromUrl()
    return {
      id: extractWrappedId(utm_custom_param ?? ''),
      name: '',
    }
  },
  target: [
    preferencesFeatureModel.setPreference,
    preferencesFeatureModel.setPreferenceShouldBeUpdatedFromInfoTrue,
  ],
})

// Set the primary region as early as possible based on the region specified in the URL.
// Example: On first visit with a region preselected in the URL (e.g., uvotv.com/arabworld),
// the corresponding region (e.g., "Arab world") should be set as the primary region if it's not set yet.
sample({
  clock: launch.appCanBeLaunched,
  source: { preference: preferencesFeatureModel.$preference },
  filter: ({ preference }) => {
    const region = getRegionFromUrl()
    return Boolean(region) && !preference
  },
  fn: () => {
    const region = getRegionFromUrl()
    return region
  },
  target: preferencesFeatureModel.setPreference,
})
